import {
    CollectedDropProps,
    dropConfigurationHandler,
    FileDropPayload,
} from "@nova/component-upload-dropbox"
import { FileExtension, GeometryType, ToolName } from "@nova/constants"
import {
    mapExtensionsToMimeType,
    supportedMimeTypes,
} from "features/job-management/uploader/constants"
import { useDrop } from "react-dnd"
import { TopBarTool } from "./TopBarTool"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveToolName, setActiveTool } from "plugins/Toolbox/tool-registry"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { usePDFRaster } from "plugins/PDFRaster/usePDFRaster"
import { useCallback, useEffect, useRef, useState } from "react"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { Point } from "@nova/data-create-polygon-kml"
import { GeoJsonTypes } from "geojson"
import { createLine } from "features/pubsub/entityActions/cubes/line"
import { encodeColorToNumber } from "@nova/serializer/src/client/util"
import {
    defaultLineColor,
    defaultPolygonFill,
    defaultPolygonOutline,
} from "plugins/Toolbox/Line/constants"
import {
    getFeaturesFromFile,
    validateFeatureFileUpload,
} from "features/data-management/lines/files"
import { v4 as randomUUID } from "uuid"
import { createGroup } from "features/pubsub/entityActions/cubes/groups"
import { createIcon } from "features/pubsub/entityActions/cubes/icon"
import { defaultIconColor } from "features/map/layers/icons/constants"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { selectUserID } from "user/profile/selectors"
import { UploadDropBox } from "plugins/shared/UploadDropBox"

const allowedFileExtensions = [FileExtension.kml, FileExtension.kmz, FileExtension.pdf]

export const TopBarUploadTool = () => {
    const dispatch = useAppDispatch()
    const activeTool = useAppSelector(selectActiveToolName())

    const userId = useAppSelector(selectUserID)

    const [canDrop, setCanDrop] = useState(false)
    const { handleDropPDF, isUploading } = usePDFRaster()
    const [isProcessingUpload, setIsProcessingUpload] = useState(false)
    const [droppedFileType, setDroppedFileType] = useState<FileExtension>()

    // sets processing to false if the relevant task is finished
    useEffectUnsafe(() => {
        if (!isUploading && droppedFileType === FileExtension.pdf) {
            setIsProcessingUpload(false)

            // Close upload menu.
            if (activeTool === ToolName.Upload) dispatch(setActiveTool(undefined))
        }
    }, [isUploading])

    const createGeometry = useCallback(
        async (
            geometry: Point[],
            holes?: Point[][],
            properties?: {
                color?: string
                fill?: string
                outline?: string
                name?: string
                type?: GeoJsonTypes
            },
            parentId?: string
        ) => {
            if (!geometry || geometry.length === 0 || !userId) return
            const isPolygon =
                properties?.type === "Polygon" || properties?.type === "GeometryCollection"
            if (isPolygon) geometry.splice(-1, 1)

            return dispatch(
                createLine({
                    data: {
                        parentId: parentId,
                        coordinates: geometry.map((point) => ({
                            longitude: point[0],
                            latitude: point[1],
                        })),
                        holes:
                            holes?.map((hole) => ({
                                coordinates: hole.map((point) => ({
                                    longitude: point[0],
                                    latitude: point[1],
                                })),
                            })) || [],
                        geometryType: isPolygon ? GeometryType.Polygon : GeometryType.Line,
                        color: properties?.color
                            ? encodeColorToNumber(properties.color)
                            : encodeColorToNumber(defaultLineColor),
                        fill: properties?.fill
                            ? encodeColorToNumber(properties.fill)
                            : properties?.color
                              ? // This sets the fill to be 0x40 alpha of the line color if a fill is not provided.
                                (((encodeColorToNumber(properties.color) >>> 0) & 0xffffff00) |
                                    0x40) >>>
                                0
                              : encodeColorToNumber(defaultPolygonFill),

                        outline: properties?.outline
                            ? encodeColorToNumber(properties.outline)
                            : properties?.color
                              ? encodeColorToNumber(properties.color)
                              : encodeColorToNumber(defaultPolygonOutline),
                        createdBy: { userId },
                    },
                    context: { name: properties?.name || "Line" },
                })
            )
        },
        [dispatch, userId]
    )

    const handleNewKMLorKMZ = useCallback(
        async (files: File[]) => {
            if (files.length === 0) return
            const [valid] = await validateFeatureFileUpload(files)
            if (valid) {
                dispatch(setActiveTool(undefined))
                const classifiedFeatures = await getFeaturesFromFile(files[0])
                if (!classifiedFeatures || classifiedFeatures.length === 0) return

                let groupId: string | undefined = undefined
                if (classifiedFeatures.length > 1) {
                    if (!userId)
                        return console.error("No userId when attempting to create a group.")
                    groupId = randomUUID()
                    dispatch(
                        createGroup({
                            data: {
                                name: files[0].name,
                                createdBy: {
                                    userId,
                                },
                            },
                            context: { id: groupId },
                        })
                    )
                }

                classifiedFeatures.slice(0, 300).forEach((geometry) => {
                    if (geometry.type === "Point" && geometry.coordinate && userId) {
                        dispatch(
                            createIcon({
                                data: {
                                    longitude: geometry.coordinate[0],
                                    latitude: geometry.coordinate[1],
                                    name: geometry.name || "unnamed icon",
                                    url: "shape/circle",
                                    color: encodeColorToNumber(defaultIconColor),
                                    parentId: groupId,
                                    createdBy: {
                                        userId,
                                    },
                                },
                            })
                        )
                    } else {
                        createGeometry(
                            geometry.geometry,
                            geometry.holes,
                            {
                                ...geometry,
                            },
                            groupId
                        )
                    }
                })

                dispatch(
                    zoomToShowPoints({
                        points: classifiedFeatures.flatMap((feature) => {
                            if (feature.coordinate) return [feature.coordinate as Point]
                            return feature.geometry
                        }),
                    })
                )
            } else {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to upload file",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 3000,
                    })
                )
            }

            setIsProcessingUpload(false)
        },
        [dispatch, createGeometry, userId]
    )

    const handleNewFiles = (files: File[]) => {
        if (files.length !== 1) return

        const file = files[0]

        setIsProcessingUpload(true)

        switch (file.name.toLowerCase().split(".").at(-1)) {
            case "pdf":
                handleDropPDF(file)
                setDroppedFileType(FileExtension.pdf)
                break
            case "kml":
            case "kmz":
                handleNewKMLorKMZ(files)
                break
        }
    }

    const [{ isOver }, dropRef] = useDrop<FileDropPayload, void, CollectedDropProps>(
        dropConfigurationHandler(
            () => {},
            allowedFileExtensions,
            () => {},
            mapExtensionsToMimeType,
            undefined,
            1
        ),
        [handleNewFiles, allowedFileExtensions]
    )

    // This will open the upload dropbox if a file is hovered over the Upload tool icon in the toolbar.
    useEffect(() => {
        if (canDrop && isOver && activeTool !== ToolName.Upload)
            dispatch(setActiveTool(ToolName.Upload))
    }, [canDrop, isOver, activeTool, dispatch])
    const ref = useRef(null)
    dropRef(ref)
    return (
        <div
            ref={ref}
            className="relative"
            onDragOver={(e) => {
                e.preventDefault()
                const items = e.dataTransfer?.items
                if (items && items.length === 1 && items[0]?.kind === "file") {
                    const { type } = items[0]
                    // type === "" when it is a .kmz
                    if (supportedMimeTypes.includes(type) || type === "") {
                        setCanDrop(true)
                        return
                    }
                }

                setCanDrop(false)
            }}
            onDragLeave={() => setCanDrop(false)}
        >
            <TopBarTool icon={Icons.Upload} name={ToolName.Upload} shortcut={{ letters: ["U"] }} />
            {activeTool === ToolName.Upload && (
                <ClickAwayListener onClickAway={() => dispatch(setActiveTool(undefined))}>
                    <UploadDropBox
                        className="absolute left-1/2 -translate-x-1/2"
                        allowedFileExtensions={allowedFileExtensions}
                        onNewItems={handleNewFiles}
                        isProcessing={isProcessingUpload}
                        maxFileSizeInMB={50}
                    />
                </ClickAwayListener>
            )}
        </div>
    )
}
